'use client'

import { useCallback, useState } from 'react'

import { Dialog } from '@graftcode/design-system'

import { CREATE_PROJECT_DIALOG_TEST_IDS } from '@common/common-test-ids.const'
import { ProjectCreatedSuccess } from '@common/components/ProjectCreatedSuccess'

import { CreateProjectForm } from '@modules/projects/forms'
import { selectCurrentWorkspace } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'

import type { Props } from './CreateProjectDialog.types'

export function CreateProjectDialog({ open, onOpenChange, onAfterClose }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentWorkspace = useWorkspacesStore(selectCurrentWorkspace)

  const [showSuccess, setShowSuccess] = useState(false)

  const handleSuccess = useCallback(
    (isFirstProject: boolean) => {
      if (isFirstProject) {
        setShowSuccess(true)
      } else {
        onOpenChange(false)
        onAfterClose?.()
      }
    },
    [onOpenChange, onAfterClose]
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    onAfterClose?.()
  }, [onOpenChange, onAfterClose])

  const handleContinue = useCallback(() => {
    setShowSuccess(false)
    onOpenChange(false)
    onAfterClose?.()
  }, [onOpenChange, onAfterClose])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isSubmitting) return
      onOpenChange(nextOpen)
      if (!nextOpen) {
        onAfterClose?.()
      }
    },
    [onOpenChange, isSubmitting, onAfterClose]
  )

  return (
    <Dialog
      noPadding
      open={open}
      data-testid={open ? CREATE_PROJECT_DIALOG_TEST_IDS.root : undefined}
      ariaLabelledBy="project-dialog-title"
      ariaDescribedBy="project-dialog-description"
      showDismiss={!showSuccess}
      onOpenChange={showSuccess ? () => {} : handleOpenChange}
    >
      {showSuccess ? (
        <ProjectCreatedSuccess workspaceName={currentWorkspace?.name || ''} onContinue={handleContinue} />
      ) : (
        <CreateProjectForm onSuccess={handleSuccess} onCancel={handleCancel} onSubmittingChange={setIsSubmitting} />
      )}
    </Dialog>
  )
}
